import { useEffect } from 'react';
import { ENTRY_POINT_UI_CLICK } from '../entryPoints';
import { ACTION_INVOKED_EVENT } from '../events';
import { useEventTracker } from './useEventTracker';

export const DATA_ATTR_ANALYTICS_BUTTON_NAME = 'data-analytics-button-name';

/** Closed `element_type` values for global click tracking. */
export const ELEMENT_TYPE_BUTTON = 'button';
export const ELEMENT_TYPE_LINK = 'link';
export const ELEMENT_TYPE_MENU_ITEM = 'menu_item';

type AnalyticsElementType = typeof ELEMENT_TYPE_BUTTON | typeof ELEMENT_TYPE_LINK | typeof ELEMENT_TYPE_MENU_ITEM;

/** Strip control, format, private-use, and surrogate characters. Keeps normal keyboard chars. */
function cleanName(raw: string): string {
    return raw.replace(/[\p{Cc}\p{Cs}\p{Cf}\p{Co}]/gu, '').trim();
}

function getButtonName(element: HTMLElement): string | null {
    const innerText = (element.textContent ?? element.innerText ?? '').trim();
    const buttonName = cleanName(innerText) || null;
    return buttonName;
}

function getCustomAnalyticsName(element: HTMLElement): string | null {
    // Explicit analytics prop takes precedence when present.
    const customName = element.getAttribute(DATA_ATTR_ANALYTICS_BUTTON_NAME)?.trim();
    return customName ? cleanName(customName) : null;
}

type TrackableElement = HTMLButtonElement | HTMLAnchorElement | HTMLLIElement;

function getClosestTrackableElement(target: EventTarget | null): TrackableElement | null {
    if (target && (target instanceof HTMLElement || target instanceof SVGElement)) {
        const el = target.closest('button, a, li[role="menuitem"]');
        if (el instanceof HTMLButtonElement || el instanceof HTMLAnchorElement || el instanceof HTMLLIElement) {
            return el;
        }
    }
    return null;
}

function getElementType(el: TrackableElement): AnalyticsElementType {
    const tag = el.tagName.toLowerCase();
    switch (tag) {
        case 'a':
            return ELEMENT_TYPE_LINK;
        case 'button':
            return ELEMENT_TYPE_BUTTON;
        case 'li':
            return ELEMENT_TYPE_MENU_ITEM;
        default:
            return ELEMENT_TYPE_BUTTON;
    }
}

function removeAllQueryParams(url: string): string {
    const urlObj = new URL(url);
    return `${urlObj.origin}${urlObj.pathname}`;
}

function getSurfacePath(): string {
    return window.location.pathname || '/';
}

/**
 * Global click tracker for buttons, links, and list items with role="menuitem" (e.g. Syncfusion / Fluent UI).
 *
 * Emits catalog event `action_invoked` with `action_name` from:
 *
 * - **Links (`<a>`):** `data-analytics-button-name` → non-empty `aria-label` → `<a>.pathname`.
 *   Link inner text is never used (may contain sensitive data).
 * - **Buttons / menu items:** `data-analytics-button-name` → non-empty `aria-label` → cleaned visible text.
 *
 * Control and unusual Unicode characters are stripped from names. Clicks with no inferable name are not tracked.
 *
 * @see ../README.md for naming conventions.
 */
export function useGlobalClickTracker() {
    const { trackEvent } = useEventTracker();

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            const el = getClosestTrackableElement(event.target);
            if (!el) {
                return;
            }
            try {
                const elementType = getElementType(el);
                const ariaLabel = el.getAttribute('aria-label')?.trim() ?? undefined;
                const properties: Record<string, unknown> = {
                    element_type: elementType,
                    surface: getSurfacePath(),
                    entry_point: ENTRY_POINT_UI_CLICK,
                };

                if (ariaLabel) {
                    properties.aria_label = ariaLabel;
                }

                if (el instanceof HTMLAnchorElement) {
                    // Strip query params — they are not useful and may contain sensitive data.
                    if (el.href) {
                        properties.href = removeAllQueryParams(el.href);
                    }
                }

                const customName = getCustomAnalyticsName(el);
                // 1. Try to get custom name
                let elementName: string | null = customName;

                // 2. Try to get aria label
                if (!elementName && ariaLabel) {
                    elementName = ariaLabel;
                }

                // 3. If no custom name or aria label, try to get pathname for links or button name
                if (!elementName) {
                    if (el instanceof HTMLAnchorElement) {
                        elementName = el.pathname || null;
                    } else {
                        elementName = getButtonName(el);
                    }
                }

                if (!elementName) {
                    return;
                }

                trackEvent(ACTION_INVOKED_EVENT, {
                    ...properties,
                    action_name: elementName,
                });
            } catch (error) {
                console.error('useGlobalClickTracker failed:', error);
            }
        };

        document.body.addEventListener('click', handleClick, true);
        return () => {
            document.body.removeEventListener('click', handleClick, true);
        };
    }, [trackEvent]);
}

export default useGlobalClickTracker;
